import { captureException } from '../../../../util/captureException'
import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatSnackBar } from '@angular/material/snack-bar'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { finalize } from 'rxjs'
import { Address4, Address6 } from 'ip-address'
import { ButtonComponent } from '../../../../components/button/button.component'
import { LoadingComponent } from '../../../../components/loading/loading.component'
import {
  Option,
  SelectComponent,
} from '../../../../components/select/select.component'
import { AssessmentStore } from '../../../../stores/assessment.store'
import {
  AmazonConnectOperationError,
  ExternalIntegrationService,
} from '../../../../services/external-integration/external-integration.service'
import { AmazonConnectIntegrationSetting } from '../../../../services/external-integration/external-integration.mapping'

export type AmazonConnectIntegrationData = {
  setting: AmazonConnectIntegrationSetting | null
}

export type AmazonConnectIntegrationDialogResult = {
  saved: boolean
}

@UntilDestroy()
@Component({
  selector: 'app-amazon-connect-integration',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIcon,
    ButtonComponent,
    SelectComponent,
    LoadingComponent,
  ],
  templateUrl: './amazon-connect-integration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./amazon-connect-integration.component.scss'],
})
export class AmazonConnectIntegrationComponent implements OnInit {
  /** 保存済みの受付状態を維持し、新規設定だけ有効にする。 */
  get active(): boolean {
    return this.data.setting == null ? true : this.data.setting.active
  }
  cidrInput = new FormControl('', { nonNullable: true })
  allowedCidrs: string[] = []
  selectedSkillMap: Option = { id: '', name: '' }
  skillMapOptions: Option[] = []
  isLoading = false
  errorMessage: string | null = null
  cidrError: string | null = null
  skillMapError: string | null = null

  /** 設定値と既存の画面用サービスを受け取る。 */
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AmazonConnectIntegrationData,
    private dialogRef: MatDialogRef<
      AmazonConnectIntegrationComponent,
      AmazonConnectIntegrationDialogResult
    >,
    private snackBar: MatSnackBar,
    private externalIntegrationService: ExternalIntegrationService,
    private assessmentStore: AssessmentStore,
  ) {}

  /** 設定を初期表示し選択可能な SkillMap を取得する。 */
  ngOnInit(): void {
    if (this.data.setting != null) {
      this.allowedCidrs = [...this.data.setting.allowedCidrs]
    }
    this.isLoading = true
    this.assessmentStore
      .getSkillMaps()
      .pipe(
        finalize(() => {
          this.isLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (skillMaps) => {
          this.skillMapOptions = skillMaps.map((map) => ({
            id: map.id,
            name: map.name,
          }))
          const selected = this.skillMapOptions.find(
            (option) => option.id === this.data.setting?.skillMapId,
          )
          if (selected) this.selectedSkillMap = selected
          else if (this.data.setting?.skillMapId != null)
            this.skillMapError =
              '設定中の SkillMap は無効化されています。別の SkillMap を選択してください'
        },
        error: (error: unknown) => {
          captureException(error)
          this.errorMessage =
            'スキルマップの取得に失敗しました。時間をおいて再度お試しください。'
        },
      })
  }

  /** backend と同じ CIDR 解析で検証し、正規化した表記で重複を判定する。 */
  addCidr(): void {
    if (this.isLoading) return
    const cidr = this.cidrInput.value.trim()
    this.cidrError = null
    const formatError = 'CIDR 形式（例: 203.0.113.0/24）で入力してください'
    if (!/^[0-9a-fA-F:.]+\/\d{1,3}$/.test(cidr)) {
      this.cidrError = formatError
      return
    }
    let address: Address4 | Address6
    try {
      address = cidr.includes(':') ? new Address6(cidr) : new Address4(cidr)
    } catch {
      this.cidrError = formatError
      return
    }
    if (
      address instanceof Address6 &&
      address.isInSubnet(new Address6('::ffff:0:0/96'))
    ) {
      this.cidrError = 'IPv4 表記で指定してください'
      return
    }
    const network = address.startAddress()
    const normalized = `${network.correctForm()}/${address.subnetMask}`
    if (address.correctForm() !== network.correctForm()) {
      this.cidrError = `ネットワークアドレスで指定してください（例: ${normalized}）`
      return
    }
    if (this.allowedCidrs.includes(normalized)) {
      this.cidrError = '同じ CIDR は追加できません'
      return
    }
    this.allowedCidrs = [...this.allowedCidrs, normalized]
    this.cidrInput.setValue('')
  }

  /** 指定された CIDR を削除する。 */
  removeCidr(cidr: string): void {
    this.allowedCidrs = this.allowedCidrs.filter((value) => value !== cidr)
  }

  /** 選択可能な SkillMap の変更だけを受け入れる。 */
  changeSkillMap(option: Option): void {
    const selected = this.skillMapOptions.find(
      (value) => value.id === option.id,
    )
    if (!selected) throw new Error('選択した SkillMap が存在しません')
    this.selectedSkillMap = selected
    this.skillMapError = null
  }

  /** 送信元を広く許可するプレフィックスを警告する。 */
  get hasUnrestrictedCidr(): boolean {
    return this.allowedCidrs.some((cidr) => {
      const prefix = Number(cidr.split('/')[1])
      return prefix < (cidr.includes(':') ? 32 : 8)
    })
  }

  /** 通信中・SkillMap 未選択・有効化時の CIDR 未登録なら保存を抑止する。 */
  isSaveDisabled(): boolean {
    return (
      this.isLoading ||
      this.selectedSkillMap.id === '' ||
      (this.active && this.allowedCidrs.length === 0)
    )
  }

  /** 設定保存後に通知し、親画面へ保存済みの結果を返す。 */
  onSubmit(event: Event): void {
    event.preventDefault()
    if (this.isSaveDisabled()) return
    this.errorMessage = null
    this.isLoading = true
    const input = {
      active: this.active,
      skillMapId: this.selectedSkillMap.id,
      allowedCidrs: [...this.allowedCidrs],
    }
    this.externalIntegrationService
      .saveAmazonConnectIntegrationSetting(input)
      .pipe(
        finalize(() => {
          this.isLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Amazon Connect 連携を保存しました。', '閉じる')
          this.dialogRef.close({ saved: true })
        },
        error: (error: unknown) => {
          captureException(error)
          this.errorMessage = this.toErrorMessage(error)
        },
      })
  }

  /** 業務エラーだけサーバーの文言を表示する。 */
  private toErrorMessage(error: unknown): string {
    return error instanceof AmazonConnectOperationError
      ? error.message
      : 'Amazon Connect 連携設定の保存に失敗しました。時間をおいて再度お試しください。'
  }

  /** 未保存のダイアログを閉じる。 */
  close(): void {
    this.dialogRef.close(undefined)
  }
}
